import StackedSheet from './StackedSheet';
import FaceStack from './FaceStack';

// EventDetailSheet — read-only event view, opened by tapping a row in
// AllEventsSheet.jsx (itself a plain page-level sheet) — so this one IS the
// right place for <StackedSheet> (one level of "opened from inside a sheet"
// stacking, per StackedSheet.jsx's own app-wide rule). Shows the full
// description, which the compact row never has room for, plus the same
// "I'm going" toggle. Leaders get an Edit button that hands off to the
// existing EventEditorSheet.
//
// Props: open, onClose, ev (occurrence or null), isModerator,
//   onToggleGoing(ev), rsvpError, onEdit(ev)

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const GOLD = 'var(--gold)';

function fmtFullDate(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function EventDetailSheet({ open, onClose, ev, isModerator, onToggleGoing, rsvpError, onEdit }) {
  const rsvpKey = ev ? `${ev.id}-${ev.occurrenceDate}` : null;
  return (
    <StackedSheet open={open} onClose={onClose} backLabel="All events" zIndex={450}>
      <div style={S.header}>
        <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: 'var(--ink)', textAlign: 'center' }}>Event</div>
        <button onClick={onClose} aria-label="Close" style={S.iconBtn}>✕</button>
      </div>
      <div style={S.scroll}>
        {ev && (
          <>
            <div style={S.title}>{ev.title}</div>
            <div style={S.metaLine}>{fmtFullDate(ev.occursAt)}</div>
            {(ev.time || ev.location) && (
              <div style={S.metaLine}>{[ev.time, ev.location].filter(Boolean).join(' · ')}</div>
            )}
            {ev.description && <div style={S.description}>{ev.description}</div>}

            {ev.goingCount > 0 && (
              <div style={S.faceRow}>
                <FaceStack userIds={ev.goingIds} profilesById={ev.goingProfilesById} size={22}/>
                <span style={S.faceCount}>{ev.going ? (ev.goingCount - 1 > 0 ? `You + ${ev.goingCount - 1} going` : "You're going") : `${ev.goingCount} going`}</span>
              </div>
            )}

            <button type="button" style={{ ...S.goingBtn, ...(ev.going ? S.goingOn : null) }} onClick={() => onToggleGoing?.(ev)}>
              {ev.going ? 'Going ✓' : "I'm going"}
            </button>
            {rsvpError?.key === rsvpKey && <div style={S.rsvpErrorText}>{rsvpError.message}</div>}

            {isModerator && (
              <button type="button" style={S.editBtn} onClick={() => onEdit?.(ev)}>Edit event</button>
            )}
          </>
        )}
      </div>
    </StackedSheet>
  );
}

const S = {
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)', minHeight: 54 },
  iconBtn: { background: 'rgba(31,27,22,0.06)', border: 'none', color: 'var(--stone)', width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 16px calc(env(safe-area-inset-bottom) + 20px)', fontFamily: SANS },
  title: { fontSize: 19, fontWeight: 800, color: 'var(--ink)', marginBottom: 8 },
  metaLine: { fontSize: 13.5, color: 'var(--stone)', marginBottom: 4 },
  description: { fontSize: 14, lineHeight: 1.5, color: '#3A332C', marginTop: 12, whiteSpace: 'pre-wrap' },
  faceRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 },
  faceCount: { fontSize: 12, color: 'var(--ink)', opacity: 0.75 },
  goingBtn: { width: '100%', marginTop: 16, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--stone)', borderRadius: 999, padding: '12px 10px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },
  goingOn: { background: 'var(--ink)', color: 'var(--white)', border: 'none' },
  rsvpErrorText: { fontSize: 12, color: '#C8372A', marginTop: 6, textAlign: 'center' },
  editBtn: { width: '100%', marginTop: 10, background: 'transparent', border: `1px solid ${GOLD}`, color: 'var(--gold-ink)', borderRadius: 999, padding: '12px 10px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },
};
